/**
 * Scroll reveal animation
 *
 * Observes direct-child sections of <main> and reveals them with a
 * fade-up transition when they enter the viewport during a downward
 * scroll. Each section animates exactly once — scrolling back up and
 * down again does NOT re-trigger the animation.
 *
 * The first section (above the fold) is excluded from the reveal entirely —
 * it renders instantly with no fade so there is no flash on initial load.
 *
 * Configuration is read from data attributes on <main>:
 *   data-scroll-animation-enabled  – "true" to activate
 *   data-scroll-animation-offset   – translateY distance in px
 *   data-scroll-animation-duration  – transition duration in ms
 *   data-scroll-animation-stagger   – per-section stagger delay in ms
 */
class ScrollReveal {
  /** @type {HTMLElement|null} */
  #main = null;

  /** @type {HTMLElement[]} */
  #sections = [];

  /** @type {number} */
  #stagger = 100;

  /** @type {number} */
  #lastScrollY = 0;

  constructor() {
    // Respect prefers-reduced-motion at the JS level as well
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // The DOM-dependent setup lives in init(). It MUST run after <main> and the
    // sections have been parsed. This script is loaded with `defer`, but we also
    // guard on readyState so the logic is correct even if the tag is ever changed
    // to `async` (an async cached script can execute before the DOM is parsed,
    // which returns null for #MainContent and silently kills the animation — the
    // "works once, dead on refresh" bug).
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init(), { once: true });
    } else {
      this.#init();
    }
  }

  #init() {
    this.#main = document.getElementById('MainContent');
    if (!this.#main || this.#main.getAttribute('data-scroll-animation-enabled') !== 'true') return;

    this.#handleReload();
    this.#readConfig();

    // Gather all direct-child shopify-section wrappers inside <main>.
    // The first section is excluded from the reveal system — it stays
    // natively visible (no .scroll-reveal class) so it never animates.
    this.#sections = Array.from(this.#main.querySelectorAll(':scope > .shopify-section')).slice(1);
    if (this.#sections.length === 0) return;

    // In the Shopify theme editor, reveal everything immediately so the
    // editor is usable (sections are ajax-loaded/reordered dynamically).
    if (window.Shopify && window.Shopify.designMode) {
      for (const section of this.#sections) {
        section.classList.add('scroll-reveal', 'scroll-reveal--visible');
      }
      return;
    }

    // Step 1: Add .scroll-reveal class to all sections (hidden state, no delay yet)
    for (const section of this.#sections) {
      section.classList.add('scroll-reveal');
    }

    // Step 2: Reveal sections already in the viewport (above-the-fold)
    this.#revealAboveFold();

    // Step 3: Assign stagger delays.
    // Above-fold sections get a cascading delay indexed only among above-fold
    // peers (0ms, 100ms, 200ms …) so the intro cascade plays correctly.
    // Below-fold sections get 0ms — they appear one at a time via the
    // IntersectionObserver, so a cumulative stagger delay would just cause a
    // visible white-space flash between intersection and animation start.
    let aboveFoldIndex = 0;
    for (const section of this.#sections) {
      if (section.classList.contains('scroll-reveal--visible')) {
        section.style.setProperty('--scroll-animation-delay', (aboveFoldIndex * this.#stagger) + 'ms');
        aboveFoldIndex++;
      } else {
        section.style.setProperty('--scroll-animation-delay', '0ms');
      }
    }

    // Track scroll direction — only animate on downward scroll
    this.#lastScrollY = window.scrollY;
    this.#observeSections();
  }

  /**
   * Leaves the reader where they were, on a reload as much as on back/forward.
   *
   * This used to force a reload back to the top so the intro cascade replayed
   * from the beginning. Losing your place on a long page costs the reader more
   * than replaying the intro gains them, so the browser's own restoration is
   * left to do its job; 'auto' is asserted rather than assumed.
   */
  #handleReload() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'auto';
    }
  }

  /**
   * Read configuration from data attributes on <main> and push them
   * into CSS custom properties on :root.
   */
  #readConfig() {
    const offset = parseInt(this.#main.getAttribute('data-scroll-animation-offset'), 10) || 30;
    const duration = parseInt(this.#main.getAttribute('data-scroll-animation-duration'), 10) || 600;
    this.#stagger = parseInt(this.#main.getAttribute('data-scroll-animation-stagger'), 10) || 100;

    // Push configuration into CSS custom properties on :root
    const root = document.documentElement;
    root.style.setProperty('--scroll-animation-offset', offset + 'px');
    root.style.setProperty('--scroll-animation-duration', duration + 'ms');
  }

  /**
   * Immediately reveal sections that are already in the viewport on
   * page load (above-the-fold content) so there is no flash of
   * invisible content.
   */
  #revealAboveFold() {
    const viewportHeight = window.innerHeight;
    for (const section of this.#sections) {
      const rect = section.getBoundingClientRect();
      // Section is at least partially visible in the viewport
      if (rect.top < viewportHeight && rect.bottom > 0) {
        section.classList.add('scroll-reveal--visible');
      }
    }
  }

  /**
   * Set up IntersectionObserver for below-the-fold sections.
   */
  #observeSections() {
    const observer = new IntersectionObserver(
      (entries) => {
        const currentScrollY = window.scrollY;
        const isScrollingDown = currentScrollY > this.#lastScrollY;
        this.#lastScrollY = currentScrollY;

        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (entry.target.classList.contains('scroll-reveal--visible')) continue;

          if (isScrollingDown) {
            // Downward scroll — play the reveal animation.
            entry.target.classList.add('scroll-reveal--visible');
          } else {
            // Scrolling up (or no vertical movement) into a not-yet-revealed
            // section: show it immediately with no animation. This keeps upward
            // scrolling un-animated while ensuring no section is ever left
            // permanently invisible.
            entry.target.classList.add('scroll-reveal--instant', 'scroll-reveal--visible');
          }
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe each section that wasn't already revealed above-the-fold
    for (const section of this.#sections) {
      if (!section.classList.contains('scroll-reveal--visible')) {
        observer.observe(section);
      }
    }
  }
}

new ScrollReveal();
